import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiFetch, setSession } from "@/lib/api";
import AuthLayout, { inputStyle, labelStyle, errorStyle } from "@/components/AuthLayout";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const goToDashboard = (session) => {
    setSession(session);
    navigate("/dashboard", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      const data = await apiFetch("/auth/login", {
        method: "POST",
        body: { email: email.trim().toLowerCase(), password },
      });
      goToDashboard(data);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setError("");
    try {
      const data = await apiFetch("/auth/google", {
        method: "POST",
        body: { credential },
      });
      goToDashboard(data);
    } catch (err) {
      setError(err.message || "Google sign-in failed. Please try again.");
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Log in to see your report, assessment status, and bonus content."
      footer={
        <p style={{ fontSize: "0.85rem", color: "#A0A0B0" }}>
          Haven't paid yet?{" "}
          <Link to="/" style={{ color: "#D4AF37" }}>
            See what's included
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} data-testid="login-form">
        <div style={{ marginBottom: "1.1rem" }}>
          <label style={labelStyle} htmlFor="login-email">Email</label>
          <input
            id="login-email"
            data-testid="login-email-input"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: "0.6rem" }}>
          <label style={labelStyle} htmlFor="login-password">Password</label>
          <input
            id="login-password"
            data-testid="login-password-input"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={{ textAlign: "right", marginBottom: "1.4rem" }}>
          <Link to="/forgot-password" style={{ fontSize: "0.82rem", color: "#A0A0B0" }}>
            Forgot password?
          </Link>
        </div>

        {error && <p style={errorStyle} data-testid="login-error">{error}</p>}

        <button
          type="submit"
          data-testid="login-submit"
          disabled={loading}
          className="cta-button"
          style={{ width: "100%", justifyContent: "center", opacity: loading ? 0.7 : 1 }}
        >
          {loading ? "Logging in…" : "Log in"}
        </button>
      </form>

      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", margin: "1.75rem 0" }}>
        <div style={{ flex: 1, height: 1, background: "rgba(212,175,55,0.18)" }} />
        <span style={{ fontSize: "0.75rem", color: "#A0A0B0", textTransform: "uppercase", letterSpacing: "0.1em" }}>
          or
        </span>
        <div style={{ flex: 1, height: 1, background: "rgba(212,175,55,0.18)" }} />
      </div>

      <GoogleSignInButton onCredential={handleGoogleCredential} onError={setError} />
    </AuthLayout>
  );
}
